import { useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { CatalogContextProvider } from './context/CatalogContext';
import {
  ClearEntityFiltersProvider,
  useOwnedGroups,
} from '@mb.io/core-components';
import {
  EntityListProvider,
  EntityUserFilter,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { CatalogHome } from './components/CatalogHome/CatalogHome';
import { BASE_ROUTE } from './constants';

const EntityListInitializer = ({ initialFilters, children }: any) => {
  const { updateFilters } = useEntityList();

  useEffect(() => {
    if (initialFilters) {
      updateFilters(initialFilters);
    }
  }, [updateFilters, initialFilters]);

  return <>{children}</>;
};

const CustomEntityListProvider = ({ children, pagination }: any) => {
  const { userGroups, loading: loadingUserGroups } = useOwnedGroups();
  const [initialFilters, setInitialFilters] = useState<any>(undefined);
  const location = useLocation();
  const hasInitializedRef = useRef(false);
  const { starredEntities } = useStarredEntities();

  useEffect(() => {
    const removeTrailingSlash = (value: string) => {
      return value.replace(/\/$/, '');
    };

    const pathname = removeTrailingSlash(location.pathname);

    if (
      !hasInitializedRef.current &&
      !loadingUserGroups &&
      (pathname === `${BASE_ROUTE}/owned` || pathname === `${BASE_ROUTE}`)
    ) {
      setInitialFilters({
        user: EntityUserFilter.owned(userGroups ?? []),
      });
      hasInitializedRef.current = true;
    }
  }, [location.pathname, loadingUserGroups, userGroups]);

  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      location.pathname === `${BASE_ROUTE}/all`
    ) {
      setInitialFilters({
        user: EntityUserFilter.all(),
      });
      hasInitializedRef.current = true;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      location.pathname === `${BASE_ROUTE}/favourites`
    ) {
      const starredEntityRefs = Array.from(starredEntities.values());

      setInitialFilters({
        user: EntityUserFilter.starred(starredEntityRefs),
      });
      hasInitializedRef.current = true;
    }
  }, [location.pathname, starredEntities]);

  if (!initialFilters) {
    return null;
  }

  return (
    <EntityListProvider pagination={pagination}>
      <EntityListInitializer initialFilters={initialFilters}>
        {children}
      </EntityListInitializer>
    </EntityListProvider>
  );
};

export const App = () => {
  return (
    <Routes>
      <Route
        path="/*"
        element={
          <CustomEntityListProvider pagination={{ mode: 'offset' }}>
            <ClearEntityFiltersProvider>
              <CatalogContextProvider>
                <CatalogHome />
              </CatalogContextProvider>
            </ClearEntityFiltersProvider>
          </CustomEntityListProvider>
        }
      />
    </Routes>
  );
};

export const NewCatalogIndexPage = <App />;
